import React, { useMemo, useState, useEffect, useRef } from "react";
import { ChevronLeft, ChevronRight, Clock } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { useSelector } from "react-redux";
import { createSelector } from "reselect";
import {
  useGetAttendanceHistoryQuery,
  useGetAttendanceStatisticsQuery,
  useGetAttendanceEntriesQuery,
} from "../../slices/attendanceApiSlice";
import { useGetUserByEmailQuery } from "../../slices/userApiSlice";
import { useGetCalendarByAcademyYearQuery } from "../../slices/calendarApiSlice";
import CustomSelect from "../../components/CustomSelect/CustomSelect";
import DateRangePicker from "../../components/DateRangePicker/DateRangePicker";
import LateReasonDialog from "../../components/LateReasonDialog/LateReasonDialog";

const selectUserInfo = (state) => state.auth.userInfo || {};
const getUserEmail = createSelector(
  selectUserInfo,
  (userInfo) => userInfo?.username || ""
);

export default function AttendanceRecordsShared({ backPath }) {
  const navigate = useNavigate();
  const [filter, setFilter] = useState("all");
  const [currentPage, setCurrentPage] = useState(1);
  const recordsPerPage = 10;
  const [showLateReasonDialog, setShowLateReasonDialog] = useState(false);
  const [selectedLateReason, setSelectedLateReason] = useState("");

  // Get user email and fetch user data
  const email = useSelector(getUserEmail);
  const { data: userByEmail } = useGetUserByEmailQuery(email, {
    skip: !email,
  });

  // Get academy ID from user data
  const academyId = userByEmail?.user?.academyId;
  const currentYear = new Date().getFullYear();

  // Check if calendar exists for current year
  const {
    data: calendarData,
    isLoading: isLoadingCalendar,
    error: calendarError,
  } = useGetCalendarByAcademyYearQuery(
    {
      academy_id: academyId,
      year: currentYear,
    },
    {
      skip: !academyId,
    }
  );

  // Check if calendar exists
  // Calendar exists if we have data and calendar object is present
  // If there's an error (like 404), it means no calendar exists
  const hasCalendar = useMemo(() => {
    if (!academyId) return false; // Can't check without academy ID
    if (isLoadingCalendar) return true; // Assume exists while loading to avoid flicker
    if (calendarError) {
      // If error is 404 or similar, no calendar exists
      const status = calendarError?.status || calendarError?.originalStatus;
      if (status === 404 || status === "FETCH_ERROR") {
        return false;
      }
      // For other errors, we might still want to show the page
      return true;
    }
    // Calendar exists if we have calendar data with a calendar object
    return (
      calendarData?.calendar !== null && calendarData?.calendar !== undefined
    );
  }, [calendarData, calendarError, isLoadingCalendar, academyId]);

  // Helper function to format date as YYYY-MM-DD without timezone issues
  const formatDateString = (date) => {
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, "0");
    const day = String(date.getDate()).padStart(2, "0");
    return `${year}-${month}-${day}`;
  };

  // Date range state (default to last 1 week)
  // Get today's date in Asia/Thimphu timezone
  const getTodayInThimphu = () => {
    const now = new Date();
    const thimphuDate = new Date(
      now.toLocaleString("en-US", { timeZone: "Asia/Thimphu" })
    );
    thimphuDate.setHours(0, 0, 0, 0);
    return thimphuDate;
  };

  const today = getTodayInThimphu();
  const oneWeekAgo = new Date(today);
  oneWeekAgo.setDate(today.getDate() - 6); // 7 days including today

  const [startDate, setStartDate] = useState(formatDateString(oneWeekAgo));
  const [endDate, setEndDate] = useState(formatDateString(today));
  const [showDateRangePicker, setShowDateRangePicker] = useState(false);
  const dateRangeButtonRef = useRef(null);

  // Fetch attendance history from API
  const {
    data: historyData,
    isLoading: isLoadingHistory,
    error: historyError,
  } = useGetAttendanceHistoryQuery(
    {
      start_date: startDate,
      end_date: endDate,
    },
    {
      skip: !startDate || !endDate, // Skip query if dates are not set
    }
  );

  // Fetch statistics from API
  const { data: statisticsData, isLoading: isLoadingStats } =
    useGetAttendanceStatisticsQuery();

  const detailBase = useMemo(() => {
    if (typeof backPath === "string" && backPath.length) {
      return backPath.replace(/\/$/, "");
    }
    return "";
  }, [backPath]);

  // Helper function to get status display label and type
  const getStatusInfo = (attendanceStatus, attendanceType) => {
    // Check attendance_type first (HOLIDAY, WEEKEND, REGULAR)
    if (attendanceType === "HOLIDAY") {
      return {
        label: "Holiday",
        type: "holiday",
        colorClass: "bg-green-100 text-green-800",
      };
    }
    if (attendanceType === "WEEKEND") {
      return {
        label: "Weekend",
        type: "weekend",
        colorClass: "bg-purple-100 text-purple-800",
      };
    }

    // Check attendance_status (PRESENT, ABSENT, LEAVE, HALF_DAY_AM, HALF_DAY_PM)
    if (attendanceStatus === "PRESENT") {
      return {
        label: "Present",
        type: "present",
        colorClass: "bg-blue-100 text-blue-800",
      };
    }
    if (attendanceStatus === "ABSENT") {
      return {
        label: "Absent",
        type: "absent",
        colorClass: "bg-red-100 text-red-800",
      };
    }
    if (attendanceStatus === "LEAVE") {
      return {
        label: "Leave",
        type: "leave",
        colorClass: "bg-gray-100 text-gray-700",
      };
    }
    if (attendanceStatus === "HALF_DAY_AM") {
      return {
        label: "Half Day Leave (AM)",
        type: "half",
        colorClass: "bg-yellow-100 text-yellow-800",
      };
    }
    if (attendanceStatus === "HALF_DAY_PM") {
      return {
        label: "Half Day Leave (PM)",
        type: "half",
        colorClass: "bg-yellow-100 text-yellow-800",
      };
    }
    // Legacy support for HALF_DAY or HALF DAY
    if (attendanceStatus === "HALF_DAY" || attendanceStatus === "HALF DAY") {
      return {
        label: "Half Day Leave",
        type: "half",
        colorClass: "bg-yellow-100 text-yellow-800",
      };
    }

    // Default fallback
    return {
      label: attendanceStatus || "Unknown",
      type: "other",
      colorClass: "bg-gray-100 text-gray-700",
    };
  };

  // Transform API response to match expected format
  const records = useMemo(() => {
    if (!historyData || !Array.isArray(historyData)) return [];
    return historyData.map((record) => {
      const statusInfo = getStatusInfo(
        record.attendance_status,
        record.attendance_type
      );
      return {
        id: record.id,
        date: record.date,
        clockIn: record.clock_in_time
          ? new Date(record.clock_in_time).toLocaleTimeString([], {
            hour: "2-digit",
            minute: "2-digit",
            timeZone: "Asia/Thimphu",
          })
          : null,
        clockOut: record.clock_out_time
          ? new Date(record.clock_out_time).toLocaleTimeString([], {
            hour: "2-digit",
            minute: "2-digit",
            timeZone: "Asia/Thimphu",
          })
          : null,
        type: statusInfo.type,
        statusLabel: statusInfo.label,
        statusColorClass: statusInfo.colorClass,
        attendance_status: record.attendance_status,
        attendance_type: record.attendance_type,
        clock_in_time: record.clock_in_time,
        clock_out_time: record.clock_out_time,
        late_reason: record.late_reason || null,
      };
    });
  }, [historyData]);

  const filtered = useMemo(() => {
    if (filter === "all") return records;
    return records.filter((r) => {
      if (filter === "present") return r.type === "present";
      if (filter === "leave") return r.type === "leave";
      if (filter === "half") return r.type === "half";
      if (filter === "absent") return r.type === "absent";
      if (filter === "holiday") return r.type === "holiday";
      if (filter === "weekend") return r.type === "weekend";
      return true;
    });
  }, [records, filter]);

  // Use statistics from API
  const workingDays = statisticsData?.totalPresentDays || 0;
  const totalDays = statisticsData?.totalWorkingDaysInYear || 260;
  // Backend now returns days directly (changed from Hours to Days in /Status API)
  // Support both new (Days) and old (Hours) field names for backward compatibility
  // totalEarnedLeaveDays and totalCasualLeaveDays represent the total available days
  const earnedLeavesTotal =
    statisticsData?.totalEarnedLeaveDays !== undefined
      ? statisticsData.totalEarnedLeaveDays
      : 22; // Fallback to default if not available
  const earnedLeavesLeft = statisticsData?.earnedLeaveBalanceRemaining || 0;
  const earnedLeavesUsed = earnedLeavesTotal - earnedLeavesLeft;

  const casualLeavesTotal =
    statisticsData?.totalCasualLeaveDays !== undefined
      ? statisticsData.totalCasualLeaveDays
      : 60; // Fallback to default if not available
  const casualLeavesLeft = statisticsData?.casualLeaveBalanceRemaining || 0;
  const casualLeavesUsed = casualLeavesTotal - casualLeavesLeft;

  // Calculate percentages
  const workingDaysPercent =
    totalDays > 0 ? Math.round((workingDays / totalDays) * 100) : 0;
  const earnedLeavesPercent = Math.round(
    (earnedLeavesLeft / earnedLeavesTotal) * 100
  );
  const casualLeavesPercent = Math.round(
    (casualLeavesLeft / casualLeavesTotal) * 100
  );

  // Pagination
  const totalPages = Math.ceil(filtered.length / recordsPerPage);
  const startIndex = (currentPage - 1) * recordsPerPage;
  const endIndex = startIndex + recordsPerPage;
  const paginatedRecords = filtered.slice(startIndex, endIndex);
  console.log(paginatedRecords);

  // Reset to page 1 when filter or date changes
  useEffect(() => {
    setCurrentPage(1);
  }, [filter, startDate, endDate]);

  // Format date for display (Asia/Thimphu timezone)
  // On phone: dd/mm/yyyy format, on desktop: "Jan 1, 2024" format
  const formatDate = (dateString, isMobile = false) => {
    if (!dateString) return "";
    try {
      const date = new Date(dateString);
      if (isMobile) {
        // dd/mm/yyyy format for mobile
        const day = String(date.getDate()).padStart(2, "0");
        const month = String(date.getMonth() + 1).padStart(2, "0");
        const year = date.getFullYear();
        return `${day}/${month}/${year}`;
      }
      return date.toLocaleDateString("en-US", {
        month: "short",
        day: "numeric",
        year: "numeric",
        timeZone: "Asia/Thimphu",
      });
    } catch (error) {
      return dateString;
    }
  };

  // Circular progress component
  const CircularProgress = ({ percentage, label, current, total }) => {
    const radius = 45;
    const circumference = 2 * Math.PI * radius;
    const offset = circumference - (percentage / 100) * circumference;

    return (
      <div className="flex flex-col items-center">
        <div className="relative w-32 h-32">
          <svg className="transform -rotate-90 w-32 h-32">
            <circle
              cx="64"
              cy="64"
              r={radius}
              stroke="#e5e7eb"
              strokeWidth="8"
              fill="none"
            />
            <circle
              cx="64"
              cy="64"
              r={radius}
              stroke="#000000"
              strokeWidth="8"
              fill="none"
              strokeDasharray={circumference}
              strokeDashoffset={offset}
              className="transition-all duration-300"
              strokeLinecap="round"
            />
          </svg>
          <div className="absolute inset-0 flex items-center justify-center">
            <span className="text-2xl font-bold text-gray-800">
              {percentage}%
            </span>
          </div>
        </div>
        <p className="text-sm font-semibold text-gray-800 mt-2">{label}</p>
        <p className="text-xs text-gray-600 mt-1">
          {current} Days / {total} total days
        </p>
      </div>
    );
  };

  // Show loading state while checking calendar or user data
  if (isLoadingCalendar || !academyId) {
    return (
      <div className="p-6 space-y-6">
        <div className="flex items-center justify-center min-h-[400px]">
          <div className="text-center">
            <p className="text-zinc-600">Loading...</p>
          </div>
        </div>
      </div>
    );
  }

  // Show message if no calendar exists (only after loading is complete and we have academyId)
  if (!hasCalendar && !isLoadingCalendar && academyId) {
    return (
      <div className="p-6 space-y-6">
        <div className="flex items-center justify-center min-h-[400px]">
          <div className="text-center max-w-md">
            <h2 className="text-2xl font-bold text-zinc-800 mb-4">
              No Calendar Available
            </h2>
            <p className="text-lg text-zinc-600">
              There is no calendar for this year so you cannot access this page.
            </p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="p-6 space-y-6">
      {/* Header with chevron and date range */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 sm:gap-4">
        <div className="flex items-center gap-3 sm:gap-4">
          <button
            onClick={() => navigate(backPath || "/technician/attendance")}
            className="w-9 h-9 sm:w-10 sm:h-10 rounded-full flex items-center justify-center text-zinc-600 hover:bg-zinc-100 hover:text-zinc-800 transition-colors"
            aria-label="Go back"
          >
            <ChevronLeft className="w-4 h-4 sm:w-5 sm:h-5" />
          </button>
          <h1 className="text-xl sm:text-2xl font-bold text-zinc-800">
            Attendance Record
          </h1>
        </div>
      </div>

      {/* Statistics with circular progress */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        <div className="bg-white rounded-lg border p-6 flex justify-center">
          <CircularProgress
            percentage={workingDaysPercent}
            label="Working days"
            current={workingDays}
            total={totalDays}
          />
        </div>
        <div className="bg-white rounded-lg border p-6 flex justify-center">
          <CircularProgress
            percentage={earnedLeavesPercent}
            label="Earned Leaves Left"
            current={earnedLeavesLeft}
            total={earnedLeavesTotal}
          />
        </div>
        <div className="bg-white rounded-lg border p-6 flex justify-center">
          <CircularProgress
            percentage={casualLeavesPercent}
            label="Casual Leaves Left"
            current={casualLeavesLeft}
            total={casualLeavesTotal}
          />
        </div>
      </div>

      {/* Records Section */}
      <div className="bg-white rounded-lg border">
        <div className="p-6 border-b">
          <div className="flex items-center justify-between mb-4">
            <div>
              <h2 className="text-xl font-bold text-zinc-800">Records</h2>
              <p className="text-sm text-zinc-600 mt-1">
                All your attendance records
              </p>
            </div>
            <div className="flex items-center gap-3">
              <CustomSelect
                options={[
                  { value: "all", label: "All" },
                  { value: "present", label: "Present" },
                  { value: "half", label: "Half Day" },
                  { value: "leave", label: "Leave" },
                  { value: "absent", label: "Absent" },
                  { value: "holiday", label: "Holiday" },
                  { value: "weekend", label: "Weekend" },
                ]}
                value={filter}
                onChange={(value) => {
                  setFilter(value);
                  setCurrentPage(1);
                }}
                placeholder="All"
                minWidth={150}
              />
              <div className="relative">
                <button
                  ref={dateRangeButtonRef}
                  onClick={() => setShowDateRangePicker(!showDateRangePicker)}
                  className="bg-white border border-gray-300 rounded-lg px-3 py-2 sm:px-4 sm:py-2 text-xs sm:text-sm text-gray-700 hover:bg-gray-50 whitespace-nowrap flex-shrink-0"
                >
                  {startDate && endDate ? (
                    <>
                      <span className="hidden lg:inline">
                        {formatDate(startDate)} - {formatDate(endDate)}
                      </span>
                      <span className="lg:hidden">
                        {formatDate(startDate, true)} -{" "}
                        {formatDate(endDate, true)}
                      </span>
                    </>
                  ) : (
                    "Select date range"
                  )}
                </button>
                <DateRangePicker
                  show={showDateRangePicker}
                  onClose={() => setShowDateRangePicker(false)}
                  startDate={startDate}
                  endDate={endDate}
                  onDateRangeSelect={({ start, end }) => {
                    if (start && end) {
                      setStartDate(start);
                      setEndDate(end);
                    }
                  }}
                  buttonRef={dateRangeButtonRef}
                  allowFutureDates={true}
                />
              </div>
            </div>
          </div>
        </div>

        {/* Table */}
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-zinc-50 border-b">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-semibold text-zinc-700 uppercase tracking-wider">
                  Date
                </th>
                <th className="px-6 py-3 text-left text-xs font-semibold text-zinc-700 uppercase tracking-wider">
                  Status
                </th>
                <th className="px-6 py-3 text-left text-xs font-semibold text-zinc-700 uppercase tracking-wider">
                  Clock in
                </th>
                <th className="px-6 py-3 text-left text-xs font-semibold text-zinc-700 uppercase tracking-wider">
                  Clock out
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-zinc-200">
              {isLoadingHistory ? (
                <tr>
                  <td
                    colSpan="4"
                    className="px-6 py-8 text-center text-zinc-500"
                  >
                    Loading records...
                  </td>
                </tr>
              ) : historyError ? (
                <tr>
                  <td
                    colSpan="4"
                    className="px-6 py-8 text-center text-red-500"
                  >
                    Error loading records:{" "}
                    {historyError?.data?.message ||
                      historyError?.message ||
                      "Please try again."}
                  </td>
                </tr>
              ) : paginatedRecords.length > 0 ? (
                paginatedRecords.map((r) => (
                  <tr
                    key={r.id}
                    className="hover:bg-zinc-50 cursor-pointer transition-colors"
                    onClick={() =>
                      navigate(
                        detailBase
                          ? `${detailBase}/record/${r.id}`
                          : `record/${r.id}`
                      )
                    }
                  >
                    <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-zinc-900">
                      {new Date(r.date + "T00:00:00").toLocaleDateString(
                        "en-GB",
                        { timeZone: "Asia/Thimphu" }
                      )}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="flex items-center gap-2">
                        <span
                          className={`px-3 py-1 rounded-full text-xs font-medium ${r.statusColorClass || "bg-gray-100 text-gray-700"
                            }`}
                        >
                          {r.statusLabel || "Unknown"}
                        </span>
                        {r.late_reason && (
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              setSelectedLateReason(r.late_reason);
                              setShowLateReasonDialog(true);
                            }}
                            className="inline-flex items-center gap-1 px-2 py-0.5 text-xs font-medium text-orange-700 bg-orange-50 border border-orange-200 rounded-full whitespace-nowrap hover:bg-orange-100 transition-colors cursor-pointer"
                            title="View late arrival reason"
                          >
                            <Clock size={12} />
                            Late
                          </button>
                        )}
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-zinc-600">
                      {r.clockIn || "-"}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-zinc-600">
                      {r.clockOut || "-"}
                    </td>
                  </tr>
                ))
              ) : (
                <tr>
                  <td
                    colSpan="4"
                    className="px-6 py-8 text-center text-zinc-500"
                  >
                    No records found
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>

        {/* Pagination */}
        {totalPages > 1 && (
          <div className="px-6 py-4 border-t flex items-center justify-between">
            <div className="flex items-center gap-2">
              <button
                onClick={() => setCurrentPage((prev) => Math.max(1, prev - 1))}
                disabled={currentPage === 1}
                className="p-2 rounded-lg border border-zinc-300 hover:bg-zinc-50 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
              >
                <ChevronLeft className="w-4 h-4" />
              </button>
              <div className="flex items-center gap-1">
                {Array.from({ length: Math.min(7, totalPages) }, (_, i) => {
                  let pageNum;
                  if (totalPages <= 7) {
                    pageNum = i + 1;
                  } else if (currentPage <= 4) {
                    pageNum = i + 1;
                  } else if (currentPage >= totalPages - 3) {
                    pageNum = totalPages - 6 + i;
                  } else {
                    pageNum = currentPage - 3 + i;
                  }
                  return (
                    <button
                      key={pageNum}
                      onClick={() => setCurrentPage(pageNum)}
                      className={`px-3 py-1 rounded-lg text-sm font-medium transition-colors ${currentPage === pageNum
                          ? "bg-zinc-900 text-white"
                          : "text-zinc-600 hover:bg-zinc-100"
                        }`}
                    >
                      {pageNum}
                    </button>
                  );
                })}
                {totalPages > 7 && currentPage < totalPages - 3 && (
                  <>
                    <span className="px-2 text-zinc-400">...</span>
                    <button
                      onClick={() => setCurrentPage(totalPages)}
                      className="px-3 py-1 rounded-lg text-sm font-medium text-zinc-600 hover:bg-zinc-100 transition-colors"
                    >
                      {totalPages}
                    </button>
                  </>
                )}
              </div>
              <button
                onClick={() =>
                  setCurrentPage((prev) => Math.min(totalPages, prev + 1))
                }
                disabled={currentPage === totalPages}
                className="p-2 rounded-lg border border-zinc-300 hover:bg-zinc-50 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
              >
                <ChevronRight className="w-4 h-4" />
              </button>
            </div>
            <div className="text-sm text-zinc-600 font-medium">
              {filtered.length} Results
            </div>
          </div>
        )}
      </div>
      <LateReasonDialog
        show={showLateReasonDialog}
        onClose={() => setShowLateReasonDialog(false)}
        lateReason={selectedLateReason}
      />
    </div>
  );
}
